import { useState } from 'react'

import {
  ActionButton,
  XStack,
  SimpleText,
  View,
  Image,
  useToastController,
  Accordion,
  Paragraph,
  styled,
  TextInputComponent,
  DropDownPickerView,
  Checkbox,
  Label,
  YStack,
  Button,
  ActiveButton
} from '@my/ui'
import { Controller } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, ChevronDown, Minus, Plus, Square, Trash2 } from '@tamagui/lucide-icons-2'
import { color, useAuthedRequestFn } from '@my/util'

import images from '../../../assets/images'
import { ConfirmationModal } from '../../../components/index'

interface RenderGoalsProps {
  type: string
  reviewData: any
  onPress: () => void
  onGoalPress: (item: any) => void
}

export const RenderGoals: React.FC<RenderGoalsProps> = ({
  type,
  reviewData,
  onPress,
  onGoalPress,
}) => {
  const [modal, setModal] = useState(false)
  const toast = useToastController()
  const queryClient = useQueryClient()
  const authedRequestFn = useAuthedRequestFn()

  const deleteFinancialGoal = useMutation({
    mutationFn: (payload: any) =>
      authedRequestFn(`/financial-goals/${payload.goalId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financial-goals'] })
      toast.show('Goal Deleted!')
    },
  })

  const handleDelete = (goalId: string) => {
    const payload = { goalTerm: type, goalId }
    deleteFinancialGoal.mutate(payload)
  }

  return (
    <View key={type} gap={15} width={'100%'} justifyContent={'center'}>
      <SimpleText
        fontSize={18}
        $platform-web={{ width: '97%' }}
        fontWeight={'600'}
        color={color.grey}
      >
        What are your{' '}
        {type === 'Short term' ? 'short-term' : type === 'Mid term' ? 'medium-term' : 'long-term'}{' '}
        financial goals (
        {type === 'Short term' ? 'next 1-3 years' : type === 'Mid term' ? '3-5 years' : '5+ years'}
        )?
      </SimpleText>
      <Label style={{lineHeight: 18}}>{type === 'Long term' ? 'Consider adding your desired Retirement Date as a long term goal!'
      : type === 'Short term' ? 'Example goals include Paying off loans, Going on vacation, or saving for a Large purchase.' : 'Do you want to Start a company one day? Purchase a home or car? Have a dream wedding?'}</Label>
      {reviewData?.map((item, idx) => (
        <View width={'100%'} key={item.goalId+idx} gap={15}>
          <XStack justifyContent="space-between" alignItems={'center'} width={'100%'} gap={20}>
            <View
              flex={1}
              borderColor={color.dullGrey}
              borderRadius={4}
              borderWidth={2}
              paddingVertical={5}
              height={45}
              justifyContent={'center'}
            >
              <ActionButton
                unstyled
                pressStyle={{ opacity: 0.5 }}
                onPress={() => onGoalPress({ ...item, type })}
              >
                <SimpleText
                  paddingHorizontal={15}
                  fontSize={14}
                  fontWeight={'400'}
                  color={color.black}
                >
                  ${item?.targetAmount}, {item?.goalName}
                </SimpleText>
              </ActionButton>
            </View>
            <ActionButton
              unstyled
              onPress={() => setModal(true)}
              pressStyle={{ opacity: 0.5 }}
              icon={<Trash2 color={'red'} />}
            />
          </XStack>
          <ConfirmationModal
            modal={modal}
            icon={
              <Image
                source={images.deletex3}
                style={{ height: 60, width: 60 }}
                resizeMode="contain"
              />
            }
            text={'Delete this goal'}
            onClose={() => setModal(false)}
            onConfirm={() => handleDelete(item.goalId)}
          />
        </View>
      ))}

      <ActiveButton style={{width: 170}} ButtonTextProps={{children: '+ Add New Goals'}} onPress={onPress} />
    </View>
  )
}

// Custom styled Accordion Trigger
const MyTrigger = styled(Accordion.Trigger, {
  name: 'MyTrigger',
  marginVertical: 5,
  p: 5,
})

// Accordion Item for each category
export const AccordionItem: React.FC<{
  value: string
  label: string
  gap?: number
  children: React.ReactNode
}> = ({ value = '', label = '', children = <></>, gap = 20 }) => (
  <Accordion.Item value={value} key={value} paddingHorizontal={20} mt={15}>
    <MyTrigger
      flexDirection="row"
      justifyContent="space-between"
      bg={color.tzaGreen}
      borderRadius={100}
      width={240}
      unstyled
    >
      {({ open }) => (
        <XStack justifyContent="space-evenly" alignItems={'center'} width={'100%'}>
          {open ? <Minus size="$1" color={'#495057'} /> : <Plus size="$1" color={'#495057'} />}
          <Paragraph fontSize={15} fontWeight={'600'} color={'#495057'}>
            {label}
          </Paragraph>
        </XStack>
      )}
    </MyTrigger>
    <Accordion.Content paddingHorizontal={0} gap={gap} width={'100%'}>
      {children}
    </Accordion.Content>
  </Accordion.Item>
)

// Helper to calculate the default monthly expenses based on mainControl data
export const getDefaultMonthlyExpense = (newExpenseOptions, linkedFinancialData: Record<string, any>) => {
  return newExpenseOptions.reduce(
    (acc, expense) => {
      acc[expense.id] = !!linkedFinancialData[`Expenses - Monthly - ${expense.id}`]
      return acc
    },
    {} as Record<string, boolean>
  )
}

export const AccordionTrigger: React.FC<{title: string}> = ({title}) => <Accordion.Trigger style={{
  flexDirection: "row",
  justifyContent: "space-between",
  borderWidth: 1, borderStyle: 'solid',
  borderColor: 'rgb(235, 235, 235)'
}} >
  {({ open }: { open: boolean }) => (
    <>
      {/* <Paragraph>1. Define your scenario goal</Paragraph> */}
      <SimpleText fontSize={16}>
        {title}
      </SimpleText>
      <XStack style={{width: 24, height: 24, border: '1px solid lightgrey', borderRadius: 2, justifyContent: 'center', alignItems: 'center'}} transition="quick" rotate={open ? '180deg' : '0deg'}>
        <ChevronDown size="$1" />
      </XStack>
    </>
  )}
</Accordion.Trigger>

export const InputField = (props) => {
  const {
    control,
    name,
    rules,
    placeholder,
    icon,
    lucideIcon,
    iconPosition,
    isPassword,
    keyboardType,
    height,
    ai,
    multiline,
    disabled,
    readOnly,
    showPlaceholder,
    disableLabel
  } = props;
  return (
    <Controller
      control={control}
      name={name}
      rules={rules ?? {}}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const rest = props.inputMode === 'numeric' ? { type: 'number' } : {};
        
        const label = !disableLabel || value ? (props?.label !== undefined ? props.label : (placeholder || '')) : '';
        return <YStack>
          {label ? <Label>{label}</Label> : null}
          <TextInputComponent
            size={14}
            height={15}
            value={value}
            InputIcon={icon}
            lucideIcon={lucideIcon}
            iconPosition={iconPosition}
            inputWidth={'100%'}
            alignItems={ai ?? 'center'}
            onChangeText={onChange}
            inputHeight={height ?? 45}
            placeholderTextColor={error ? color.lightRed : color.black}
            image={{ eyeOff: images.eyeOff, eyeOn: images.eyeOn }}
            keyboardType={keyboardType ?? 'default'}
            inputMode={props.inputMode}
            isPassword={isPassword ?? false}
            placeHolderText={showPlaceholder ? placeholder : ''}
            tintColor={error ? color.lightRed : /* color.black  */'rgb(133, 193, 157)'}
            justifyContent="flex-start"
            color={error ? color.lightRed : disabled ? color.opaqueGrey : color.black}
            multiline={multiline}
            borderColor={error ? color.lightRed : color.dullGrey}
            disabled={disabled}
            readOnly={readOnly}
            style={{
              flexGrow: 1, cursor: readOnly ? 'not-allowed' as any : 'auto',
              //backgroundColor: 'rgb(248, 248, 248)',
              height: height ?? 45, 
              //borderBottomWidth: 2, borderBottomStyle: 'solid', borderBottomColor: error ? color.lightRed : 'rgb(133, 193, 157)'
            }}
            {...rest}
          />
        </YStack>
      }}
    />
  )
}

// DropdownField Component
export const DropdownField: React.FC<DropdownFieldProps> = (props) => {
  const { control, name, rules, icon, placeholder, items, native, showLabel } = props
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const label = value || showLabel ? (props?.label !== undefined ? props.label : (placeholder || '')) : '';
        return <YStack>
          {label ? <Label htmlFor={name}>{label}</Label> : null}
          <DropDownPickerView
            className='financialprofile-dropdown'
            items={items}
            value={value}
            setValue={onChange}
            placeholder={placeholder}
            InputIcon={icon}
            tintColor={error ? color.lightRed : /* color.black */'rgb(133, 193, 157)'}
            iconColor={error ? color.lightRed : /* color.black */'rgb(133, 193, 157)'}
            bottomBorderColor={error ? color.lightRed : /* color.black */'rgb(224, 225, 230)'}
            native={native}
            TriggerStyle={{cursor: 'pointer'}}
          />
        </YStack>
      }}
    />
  )
}

// CheckboxWithLabel Component
export const CheckboxComponent: React.FC<{
  expenseItem: any
  index: number
  control: any
  value: boolean
  onChange: any
}> = ({ expenseItem, index, control, value, onChange }) => (
  <XStack width={180} gap={10}>
    <Checkbox
      size="$4"
      gap="$2"
      checked={value}
      onCheckedChange={onChange}
      id={`switch-${index}-${expenseItem.label}`}
      backgroundColor={value ? color.tzaGreen : color.dullGrey}
    >
      <Checkbox.Indicator>
        <Check color={color.white} size={15} />
      </Checkbox.Indicator>
    </Checkbox>
    <SimpleText flex={1} fontSize={16} color={color.black}>
      {expenseItem.label}
    </SimpleText>
  </XStack>
)
